/**
 * PC の見積書の表の列（#131、docs/47 §12・docs/10 §9.1）。Figma「見積（スマホ・PC）10/3」PC2 簡潔版（53:201）。
 *   ・紙の列（顧客に出す 8 列）と社内用の列（仕入れ・仕入元）を、この定義だけで分ける。
 *     見出し（thead）・列の幅（colgroup）・表の最小の幅・行の colSpan はここから作る
 *   ・名称だけ幅を決めない（残りを全部もらう。最小 240）。ほかは px で固定（table-layout: fixed＝名称に長い文字を入れても他の列の幅は変わらない）。
 *     書いている行の入力欄も同じ幅（欄は列の幅いっぱい）
 *   ・全列で 1,150px。狭いと表の中で横に流し、No と名称は左に固定
 *   ・#139（10/5 射場さん）：名称 300 → 240（伸びる）・規格・仕様 150 → 200・単位 56 → 72（「セット」が切れない）。
 *     社内用は利益率の列を無くし、仕入れの欄 1 つ（100）に 上段＝仕入れの額・下段＝利益率（小さい文字）。見出しは「仕入れ」だけ
 *   ・#140（10/6 射場さん）：名称の最小 240 → 220（1280px で横に流れない）。規格・仕様と備考の幅は人ごとに変えられる
 *     （見出しの境目のドラッグ・見出しの「…」。個人設定 estimateColumns。上限は contract の ESTIMATE_COLUMN_LIMITS）。社内用の列は変えない
 *   ・T2（税の画面と紙、../eigyouryoku/docs/14）：使う機能「軽減税率・税区分」（reducedTax）が ON の会社だけ、金額の右に「税区分」（72）。
 *     紙には列として出さない（紙は ※ と内訳で表す）。OFF の会社は行の詳細（2段目）の「税区分」から選ぶ
 */
import { ESTIMATE_COLUMN_LIMITS, ESTIMATE_PC_MSG, TAX_MSG, type EstimateColumns } from "@genbaryoku/contract";
import { lineSpecLabel, t } from "../../labels.js";

export type ColKey = "no" | "name" | "spec" | "qty" | "unit" | "price" | "amount" | "tax" | "note" | "cost" | "source" | "origin" | "rqmore";
export type ColDef = {
  key: ColKey;
  /** 見出しの文字（No は空でなく「No」）。名称・規格・備考は会社の言葉（{lineName} など。#222＝columnsFor・rfqColumns が t() で埋める） */
  label: string;
  /** 見出しの下の小さな字（#218：見積依頼の単価・金額の「回答欄」） */
  sub?: string;
  /** px。null は残りを全部（名称） */
  width: number | null;
  /** width が null のときの最小（表の最小の幅に足す） */
  min?: number;
  /** 右寄せ（数字） */
  r?: boolean;
  /** 横に流しても左に固定 */
  sticky?: boolean;
  group: "paper" | "internal";
};

export const PAPER_COLS: readonly ColDef[] = [
  { key: "no", label: "No", width: 40, sticky: true, group: "paper" },
  { key: "name", label: "{lineName}", width: null, min: 220, sticky: true, group: "paper" },
  { key: "spec", label: "{lineSpec}", width: ESTIMATE_COLUMN_LIMITS.spec.def, group: "paper" }, // 画面は lineSpecLabel（既定なら「規格・仕様」）
  { key: "qty", label: "数量", width: 72, r: true, group: "paper" },
  { key: "unit", label: "単位", width: 72, group: "paper" },
  { key: "price", label: "単価", width: 88, r: true, group: "paper" },
  { key: "amount", label: "金額", width: 100, r: true, group: "paper" },
  { key: "note", label: "{lineNote}", width: ESTIMATE_COLUMN_LIMITS.note.def, group: "paper" },
];
/** 税区分の列（T2）。金額の右・幅 72。紙の 8 列には入れない（画面の表だけ） */
/** 税区分の列（T2）。72px＝列は前の短い名前（軽減・新聞…＝#304。#303 で 96px にしたのを戻した）。「軽減(食料)」の形は乗せたときと行の詳細 */
export const TAX_COL: ColDef = { key: "tax", label: TAX_MSG.category, width: 72, group: "paper" };
export const INTERNAL_COLS: readonly ColDef[] = [
  /** 上段＝仕入れの額（自社・未入力・— も上段）、下段＝利益率（原価割れは朱の文字） */
  { key: "cost", label: "仕入れ", width: 100, r: true, group: "internal" },
  { key: "source", label: ESTIMATE_PC_MSG.colSource, width: 138, group: "internal" },
];

/**
 * 金額の列（#190、10/16 射場さん「金額は絶対に省略しない」）：単価・金額・仕入れは、その見積でいちばん長い額が入る幅まで広げる
 * （既定の幅が最小。表の useMoneyFit が見本の字で測る）。小計・合計・「○○ 計」の行は単価〜金額の2列を結合して額を出すので数えない
 */
export const MONEY_KEYS = ["price", "amount", "cost"] as const;
export type MoneyKey = (typeof MONEY_KEYS)[number];
export type MoneyTexts = Partial<Record<MoneyKey, string | undefined>>;
/** 字の幅の重み：数字・カンマは半角、「−」（U+2212）など半角でない字は全角＝2 */
const moneyWeight = (x: string): number => [...x].reduce((a, ch) => a + (/[0-9,.\-]/.test(ch) ? 1 : 2), 0);
/** いちばん長い額（字の重みで比べる。無ければ undefined） */
export const longestMoney = (xs: Iterable<string>): string | undefined => {
  let best: string | undefined;
  for (const x of xs) if (x && (best == null || moneyWeight(x) > moneyWeight(best))) best = x;
  return best;
};

/** 幅を変えられる列（#140） */
export type SizableKey = "spec" | "note";
export const SIZABLE: ReadonlySet<ColKey> = new Set<ColKey>(["spec", "note"]);
export const DEFAULT_WIDTHS: EstimateColumns = { spec: ESTIMATE_COLUMN_LIMITS.spec.def, note: ESTIMATE_COLUMN_LIMITS.note.def };
/** 表の列。widths で 規格・仕様／備考 の幅を上書きする（人の設定。無ければ既定）。taxCol で金額の右に「税区分」（T2）。見出しは会社の言葉（#222） */
export const columnsFor = (showInternal: boolean, widths: EstimateColumns = DEFAULT_WIDTHS, taxCol = false): readonly ColDef[] => {
  const paper = taxCol ? PAPER_COLS.flatMap((c) => (c.key === "amount" ? [c, TAX_COL] : [c])) : PAPER_COLS;
  return (showInternal ? [...paper, ...INTERNAL_COLS] : paper).map((c) => ({ ...c, label: c.key === "spec" ? lineSpecLabel() : t(c.label), ...(c.key === "spec" || c.key === "note" ? { width: widths[c.key] } : {}) }));
};
export const minWidthOf = (cols: readonly ColDef[]): number => cols.reduce((a, c) => a + (c.width ?? c.min ?? 0), 0);
/** 名称の左の固定の位置（No の幅） */
export const NAME_LEFT = PAPER_COLS[0]!.width!;
/** 紙の列の数（見出し・文章の行は No のあとを全部つなぐ＝PAPER − 1） */
export const PAPER_SPAN = PAPER_COLS.length;
export const INTERNAL_SPAN = INTERNAL_COLS.length;

/**
 * 見積依頼書の明細の列（#218、docs/88 §4.2・Figma ②改 10/20）：見積書と同じ外枠（EstimateTable）に、見積依頼の列の定義を1つ足す。
 * 単価・金額は「回答欄」（打てない・{vendor}が書く）。社内用の列は無い。右に「元」（見積 行 n／追加）と行の「⋯」。
 * 規格・備考の幅は見積書と同じ人ごとの設定（estimateColumns）で上書きし、見出しの境で動かす。名称が余りを受ける
 */
export const RFQ_COLS: readonly ColDef[] = [
  { key: "no", label: "No", width: 40, sticky: true, group: "paper" },
  { key: "name", label: "{lineName}", width: null, min: 200, sticky: true, group: "paper" },
  { key: "spec", label: "{lineSpec}", width: ESTIMATE_COLUMN_LIMITS.spec.def, group: "paper" },
  { key: "qty", label: "数量", width: 90, r: true, group: "paper" },
  { key: "unit", label: "単位", width: 80, group: "paper" },
  { key: "price", label: "単価", sub: "回答欄", width: 110, r: true, group: "paper" },
  { key: "amount", label: "金額", sub: "回答欄", width: 120, r: true, group: "paper" },
  { key: "note", label: "{lineNote}", width: ESTIMATE_COLUMN_LIMITS.note.def, group: "paper" },
  { key: "origin", label: "元", width: 110, group: "paper" },
  { key: "rqmore", label: "", width: 44, group: "paper" },
];
/** 見積依頼書の列（#218）。規格・備考の幅は人の設定（rfqColumns）、見出しは会社の言葉（#222） */
export const rfqColumnsFor = (widths: EstimateColumns): readonly ColDef[] =>
  RFQ_COLS.map((c) => ({ ...c, label: t(c.label), ...(c.key === "spec" || c.key === "note" ? { width: widths[c.key] } : {}) }));

/**
 * 仕入の明細の列（#233 3a、docs/93 §15.10 ①）：見積書と同じ外枠（EstimateTable）に、仕入の列の定義を1つ足す。
 * 数量・単価・金額は打てる（金額は相手の請求書のとおり＝数量×単価は既定）。使う機能「軽減税率・税区分」の会社だけ金額の右に「税区分」。
 * 右に「元」（{order} 行 n／追加）と行の「⋯」。規格・備考の幅は見積書と同じ人ごとの設定（estimateColumns）
 */
export const PURCHASE_COLS: readonly ColDef[] = [
  { key: "no", label: "No", width: 40, sticky: true, group: "paper" },
  { key: "name", label: "{lineName}", width: null, min: 200, sticky: true, group: "paper" },
  { key: "spec", label: "{lineSpec}", width: ESTIMATE_COLUMN_LIMITS.spec.def, group: "paper" },
  { key: "qty", label: "数量", width: 90, r: true, group: "paper" },
  { key: "unit", label: "単位", width: 80, group: "paper" },
  { key: "price", label: "単価", width: 110, r: true, group: "paper" },
  { key: "amount", label: "金額", width: 120, r: true, group: "paper" },
  { key: "note", label: "{lineNote}", width: ESTIMATE_COLUMN_LIMITS.note.def, group: "paper" },
  { key: "origin", label: "元", width: 120, group: "paper" },
  { key: "rqmore", label: "", width: 44, group: "paper" },
];
/** 仕入の明細の列（規格・備考の幅は人の設定、taxCol で金額の右に「税区分」、見出しは会社の言葉） */
export const purchaseColumnsFor = (widths: EstimateColumns, taxCol: boolean): readonly ColDef[] =>
  (taxCol ? PURCHASE_COLS.flatMap((c) => (c.key === "amount" ? [c, TAX_COL] : [c])) : PURCHASE_COLS).map((c) => ({ ...c, label: t(c.label), ...(c.key === "spec" || c.key === "note" ? { width: widths[c.key] } : {}) }));
